import { memo, useCallback, useMemo } from 'react';
import { formControlClasses, inputClasses, selectClasses } from '@mui/material';
import { Stack } from '@mui/system';
import { RackLoadTypeSpec } from '@thive/fddm-types';
import { useShallow } from 'zustand/react/shallow';

import { SelectInput } from '@/modules/common/components/inputs';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  isRackingArea,
  isSingularStationArea,
} from '@/modules/floorplanDesign/helpers';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { getLoadCarrierByArea } from '@/modules/floorplanDesign/helpers/loadCarrier';
import { isManualArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { logger } from '@common/logging/logger';

const containerSx = {
  // Wrap value-string to new line, don't truncate.
  [`& .${selectClasses.multiple}`]: {
    whiteSpace: 'normal!important',
  },

  // because value-string is wrapped, correct the height and padding of the options container.
  [`& .${formControlClasses.root} .${inputClasses.root}`]: {
    height: 'auto',
    padding: '4px 12px!important',
  },
};

const LoadTypeComponent = () => {
  const allLoadCarriers = useLoadCarrierStore((state) => state.allLoadCarriers);
  const selectedAreas = useReadSelectedAreas();
  const { loadTypesInStore, getLoadTypeById } = useFloorplanDesignStore(
    useShallow((state) => ({
      loadTypesInStore: state.setup.loadTypes,
      getLoadTypeById: state.getLoadTypeById,
    })),
  );
  const enabledLoadTypeIds = loadTypesInStore.map((lt) => lt.id);
  const options = allLoadCarriers.getByIds(enabledLoadTypeIds).map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const loadType: string | undefined = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];

    return getLoadCarrierByArea(area);
  }, [selectedAreas]);

  const onChange = useCallback(
    (newValue: string) => {
      const areas = areasToFlatCollection(selectedAreas);
      const area = areas.at(0);

      if (areas.length !== 1 || !area) return;

      const loadType = getLoadTypeById(newValue);
      if (!loadType) {
        logger.error();
        return;
      }

      const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

      if (isSingularStationArea(area) || isManualArea(area)) {
        fddmSvc.updateArea({
          ...area,
          stationSpec: {
            ...area.stationSpec,
            loadTypeIds: [newValue],
            width: loadType.width,
            length: loadType.length,
          },
        });
      } else if (isRackingArea(area)) {
        const loadTypes = minItemsArrayUtils.mapAtLeast1<string, RackLoadTypeSpec>(
          [newValue],
          (value, index) => ({
            loadTypeId: value,
            stationRegex: area.rackSpec.loadTypes[index]?.stationRegex || undefined,
          }),
        );

        fddmSvc.updateArea({
          ...area,
          rackSpec: {
            ...area.rackSpec,
            loadTypes,
          },
        });
      }
    },
    [getLoadTypeById, selectedAreas],
  );

  if (loadType === undefined) {
    logger.error('tried to render loadType component but a load type was not found');
    return null;
  }

  if (!options) {
    return null;
  }

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_carrier_select.label'>
      <Stack sx={containerSx}>
        <SelectInput options={options} onChange={onChange} value={loadType} />
      </Stack>
    </PropertiesLabel>
  );
};

export const LoadType = memo(LoadTypeComponent);
